import React, {useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from "react-intl";
import {HttpStatus} from "../../../../../modules/common/enums/HttpStatus";
import {DeputyDto} from "../../../../../modules/workflow/dto/DeputyDto";
import {DeputyApi} from "../../../../../modules/workflow/api/DeputyApi";
import {Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {MessageUtils} from "../../../../../utils/MessageUtils";
import CancelButton from "../../../../../components/buttons/CancelButton";
import SaveButton from "../../../../../components/buttons/SaveButton";
import FormFieldText from "../../../../../components/FormFieldText";
import SearchIconButton from "../../../../../components/buttons/SearchIconButton";
import CloseIconButton from "../../../../../components/buttons/CloseIconButton";
import FormFieldDate from "../../../../../components/FormFieldDate";
import dayjs from "dayjs";
import {Formats} from "../../../../../constants/Formats";
import UserSelectDialog from "../../../../account/components/UserSelectDialog";
import {UserDto} from "../../../../../modules/account/dto/UserDto";

export type DeputyDetailDialogProps = {
    open: boolean;
    deputy: DeputyDto;
    onClose: () => void;
    onOk: () => void;
};

const DeputyDetailDialog: React.FC<DeputyDetailDialogProps> = (props) => {
    const intl = useIntl();
    const title = props.deputy.id ? 'WORKFLOW_DEPUTY_MODIFY' : 'WORKFLOW_DEPUTY_ADD';

    const [innerDeputy, setInnerDeputy] = useState<DeputyDto>(props.deputy);

    const [messageMap, setMessageMap] = useState(new Map<string, string>());

    const [openUserSelectDialog, setUserSelectDialog] = useState(false);

    useEffect(() => {
        setInnerDeputy({...props.deputy});
    }, [props.deputy]);

    const handleChange = (name: string, value: any) => {
        setInnerDeputy(previousDeputy => ({...previousDeputy, [name]: value}));
    };

    const handleOk = async () => {
        if (validate()) {
            if (props.deputy.id) {
                const responseResult = await DeputyApi.updateDeputy(innerDeputy);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'COMMON_MODIFY_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            } else {
                const responseResult = await DeputyApi.createDeputy(innerDeputy);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'COMMON_ADD_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            }
        }
    };

    const handleCancel = () => {
        props.onClose();
    };

    const validate = () => {
        const newMessageMap = new Map<string, string>();
        if (innerDeputy.agentName === '') {
            newMessageMap.set('agentName', intl.formatMessage({id: 'WORKFLOW_DEPUTY_AGENT_NAME_REQUIRED'}));
        }
        if (innerDeputy.startTime === '') {
            newMessageMap.set('startTime', intl.formatMessage({id: 'WORKFLOW_DEPUTY_START_TIME_REQUIRED'}));
        }
        if (innerDeputy.endTime === '') {
            newMessageMap.set('endTime', intl.formatMessage({id: 'WORKFLOW_DEPUTY_END_TIME_REQUIRED'}));
        }

        setMessageMap(newMessageMap);
        return newMessageMap.size <= 0;
    };

    const handleClearAgent = () => {
        const newDeputy = Object.assign({}, innerDeputy);
        newDeputy.agentUserId = '';
        newDeputy.agentName = '';
        newDeputy.agentMobile = '';
        newDeputy.agentEmail = '';
        setInnerDeputy(newDeputy);
    }

    const handleSelectAgent = (user: UserDto) => {
        const newDeputy = Object.assign({}, innerDeputy);
        newDeputy.agentUserId = user.id;
        newDeputy.agentName = user.fullName;
        newDeputy.agentMobile = user.mobile;
        newDeputy.agentEmail = user.email;
        setInnerDeputy(newDeputy);
    }

    return (
        <Dialog open={props.open} onClose={handleCancel} fullWidth={true}>
            <DialogTitle>
                {intl.formatMessage({id: title})}
            </DialogTitle>
            <DialogContent>
                <UserSelectDialog open={openUserSelectDialog}
                                  onSelect={(value) => handleSelectAgent(value)}
                                  onClose={() => setUserSelectDialog(false)}/>
                <FormFieldText label={intl.formatMessage({id: "WORKFLOW_DEPUTY_AGENT_NAME"})}
                               required={true} errorText={messageMap.get("agentName")}
                               value={innerDeputy.agentName}
                               disabled={true}
                               endAdornment={
                                   <div>
                                       <SearchIconButton onClick={() => setUserSelectDialog(true)}/>
                                       <CloseIconButton onClick={() => handleClearAgent()}/>
                                   </div>
                               }/>
                <FormFieldText label={intl.formatMessage({id: "WORKFLOW_DEPUTY_AGENT_MOBILE"})}
                               disabled={true}
                               value={innerDeputy.agentMobile}/>
                <FormFieldText label={intl.formatMessage({id: "WORKFLOW_DEPUTY_AGENT_EMAIL"})}
                               disabled={true}
                               value={innerDeputy.agentEmail}/>
                <FormFieldDate label={intl.formatMessage({id: "WORKFLOW_DEPUTY_START_TIME"})}
                               required={true} errorText={messageMap.get("startTime")}
                               value={dayjs(innerDeputy.startTime, Formats.DATE_SIMPLE)}
                               minDate={dayjs()}
                               onChange={(value) => handleChange("startTime", value + "000000")}/>
                <FormFieldDate label={intl.formatMessage({id: "WORKFLOW_DEPUTY_END_TIME"})}
                               required={true} errorText={messageMap.get("endTime")}
                               value={dayjs(innerDeputy.endTime, Formats.DATE_SIMPLE)}
                               minDate={innerDeputy.endTime !== "" ? dayjs(innerDeputy.endTime, Formats.DATE_SIMPLE) : dayjs()}
                               onChange={(value) => handleChange("endTime", value + "235959")}/>
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={handleCancel}/>
                <SaveButton onClick={handleOk}/>
            </DialogActions>
        </Dialog>
    );
};

export default DeputyDetailDialog;
